/**
 * Recruiter engagement gate, frontend half.
 *
 * The backend refuses a company engagement action with a structured 403 (see
 * backend/docs/2026-08-19-recruiter-engagement-gate-design.md, "Refusal
 * contract"):
 *
 *   { statusCode: 403, code, message, hasAnyListings }
 *
 * That is the same shape GroupApprovedGuard already throws for
 * GROUP_NOT_APPROVED, so it arrives the same way: AllExceptionsFilter re-wraps
 * every HttpException, which puts the thrown body one level down under
 * `message`. A caught axios error therefore reads
 *
 *   err.response.data.message.code
 *
 * and NOT `err.response.data.code`. api.js's own 403 interceptor already
 * handles both levels defensively, and codeOf() in httpErrorEnvelope does the
 * same, so this module reuses it rather than picking one shape and being wrong
 * the day the filter changes. Note handleApiError() (errorHandler.js) keeps
 * `.response` and `.status` on the Error it re-throws but does NOT copy `code`
 * up, which is why nothing here reads `err.code`.
 *
 * Four codes rather than one because the next action differs. The copy lives
 * here rather than being taken from the server's `message` string: these
 * surfaces are bound by house copy rules (no em dashes, "listing" never "job",
 * "recruiter" never "facility", and never a word about what the provider asked
 * for), and rendering server-authored prose would put those rules out of reach
 * of a frontend test.
 */
import { codeOf } from './httpErrorEnvelope';

export const NO_LIVE_LISTING = 'NO_LIVE_LISTING';
export const NO_MATCHING_LISTING = 'NO_MATCHING_LISTING';
export const LISTING_NOT_YOURS = 'LISTING_NOT_YOURS';
// The listing is held by the rate-review queue: its posted rate tripped the
// review hold, so the pair gate blocks it from reaching providers until a
// human clears it. Its own code because the next action is unlike the other
// three: there is nothing for the recruiter to post or adjust, and saying
// "does not meet what this provider is looking for" about a held listing
// would be false.
export const RATE_UNDER_REVIEW = 'RATE_UNDER_REVIEW';

/**
 * The listings ROSTER. Every gate refusal resolves here, and each copy branch
 * now carries its own `ctaHref` so the destination travels with the label it
 * was written for and the two cannot drift apart. They had: RateCheckPanel
 * took `ctaLabel` from this module and then supplied its own href beside it,
 * eight lines apart.
 *
 * THE VALUE MUST STAY A BARE PATHNAME. Three readers compare against it with
 * `===` -- CompanyLayout arms its claim-modal suppression with a pathname,
 * JobFeedApprovalBridge compares `location.pathname`, and jobFeedApprovalService
 * matches a notification's actionUrl -- so a query string folded in here breaks
 * all three silently. A builder URL is a SEPARATE derived constant, the way
 * CompanyLayout does it.
 */
export const COMPANY_LISTINGS_PATH = '/company/listings';

const GATE_CODES = [
  NO_LIVE_LISTING,
  NO_MATCHING_LISTING,
  LISTING_NOT_YOURS,
  RATE_UNDER_REVIEW,
];

const isPlainObject = (v) =>
  Boolean(v) && typeof v === 'object' && !Array.isArray(v);

/**
 * `hasAnyListings` rides alongside the code and, like the code, may sit at
 * either level of the envelope. Anything that is not an actual boolean reads as
 * null (unknown) rather than false: false is a claim about the account and must
 * never be inferred from a missing field.
 */
const hasAnyListingsOf = (body) => {
  if (typeof body?.hasAnyListings === 'boolean') return body.hasAnyListings;
  const nested = body?.message;
  if (isPlainObject(nested) && typeof nested.hasAnyListings === 'boolean') {
    return nested.hasAnyListings;
  }
  return null;
};

/**
 * Read a refusal off a caught error, or null if this was not one.
 *
 * Null is the important half of the contract. A network failure, a 500, a
 * timeout, an aborted request and a 403 carrying some other code all return
 * null, so a caller can never render "you have no live listing" on the strength
 * of a request that never got an answer. Only a 403 carrying a code we
 * recognise counts.
 */
export const parseEngagementRefusal = (error) => {
  const status = error?.response?.status ?? error?.status;
  if (status !== 403) return null;
  const body = error?.response?.data;
  const code = codeOf(body);
  if (!GATE_CODES.includes(code)) return null;
  return { code, hasAnyListings: hasAnyListingsOf(body) };
};

const NOT_YOURS_TITLE = 'That listing is not available here';

/**
 * Copy for a refusal, or null for a code we do not recognise.
 *
 * LISTING_NOT_YOURS is deliberately vague about the listing: the refusal must
 * not confirm whether that id exists.
 *
 * EVERY BRANCH CARRIES ITS OWN `ctaHref`. Today they all resolve to the roster,
 * and that is a finding rather than a shortcut: on all four codes the server
 * has already proved the recruiter owns a listing, so sending them to a blank
 * builder would tell them to re-do work they have done. See each branch.
 *
 * `hasAnyListings` is only trustworthy on NO_LIVE_LISTING. That is the one code
 * the gate measures it for (`hasAnyLiveListing`, a real countDocuments); on
 * every other code the server passes a constant, so nothing here may read it.
 */
export const resolveEngagementGateCopy = ({ code, hasAnyListings } = {}) => {
  if (code === NO_LIVE_LISTING) {
    // THE ROSTER, NOT THE BUILDER, on both branches. The gate proved ownership
    // of the named listing before it threw, so it is theirs and merely
    // unpublished -- and every internally-posted listing is born unpublished.
    // A blank builder here says "you don't have any listings yet", which is
    // false and sends them to re-post work they have already done. The roster
    // is also the only surface that SHOWS an unpublished listing: it applies
    // no reviewStatus filter, while the gate's own query does, so the draft
    // they need is visible there and was never in the set the gate looked at.
    //
    // `=== true` strictly, so an unknown (null) falls to the safe side.
    return hasAnyListings === true
      ? {
          // The old copy said "None of your listings are live right now" on
          // this branch too, which the gate's own measurement contradicts: it
          // reports true only when it counted at least one live listing. The
          // named listing is the unpublished one, not all of them.
          title: 'That listing is not live right now',
          body: 'This listing is not published, and you have others that are. Pick a live one, or publish this one from your listings.',
          ctaLabel: 'Go to your listings',
          ctaHref: COMPANY_LISTINGS_PATH,
        }
      : {
          title: 'Your deck needs a live listing',
          body: 'None of your listings are live right now. Publish one from your listings, or post a new one there, and the deck opens up.',
          ctaLabel: 'Go to your listings',
          ctaHref: COMPANY_LISTINGS_PATH,
        };
  }

  if (code === NO_MATCHING_LISTING) {
    // The roster serves BOTH halves of this label, which is why the label is
    // allowed to name two actions: the listing that failed is theirs, live and
    // editable, and the New listing button sits on the same page. It is also
    // the accurate advice -- what failed is this listing's own pay or its
    // facility's location, so adjusting it is the direct fix, where publishing
    // some other draft is not guaranteed to clear the same gate.
    return {
      title: 'No listing of yours fits this provider',
      body: 'Your live listings do not meet what this provider is looking for. Adjust one, or post another.',
      ctaLabel: 'Adjust or post a listing',
      ctaHref: COMPANY_LISTINGS_PATH,
    };
  }

  if (code === RATE_UNDER_REVIEW) {
    // Honest about whose court the ball is in: the hold is ours, not theirs.
    // No figure and no threshold, and nothing about what any provider is
    // looking for, because none of that is why the swipe was refused.
    return {
      title: 'The rate on this listing is being reviewed',
      body: 'The posted rate on this listing needs a quick review by our team before it can reach providers. There is nothing you need to change. If it stays under review, contact support and we will take a look.',
      ctaLabel: 'Go to your listings',
      ctaHref: COMPANY_LISTINGS_PATH,
    };
  }

  if (code === LISTING_NOT_YOURS) {
    // ONE BRANCH, not two. This code used to split on `hasAnyListings === false`
    // and tell that half to "post a listing of your own to start browsing" --
    // but the server hardcodes `false` at all three places it raises this code
    // and never counts anything, because the flag is NON-DISCLOSURE here, not a
    // measurement. One of those three fires on an unusable caller id before any
    // database read at all. So a recruiter with a full roster who follows a
    // stale or foreign listing link was told they had no listings and sent off
    // to post their first one.
    //
    // The surviving copy is the half that is true whatever they own: nothing
    // they post or publish can make the named id theirs, so the only useful
    // next move is to pick one of their own. Restore the split only if the
    // server starts measuring the CALLER's own inventory on this path.
    return {
      title: NOT_YOURS_TITLE,
      body: 'We could not open that listing for your account. Pick one of your own listings to keep browsing.',
      ctaLabel: 'Go to your listings',
      ctaHref: COMPANY_LISTINGS_PATH,
    };
  }

  return null;
};
